import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './auth/require-auth.js';
import { AppShell } from './components/AppShell.js';
import './components/ui.css';

import { AlertsPage } from './pages/AlertsPage.js';
import { ChatPage } from './pages/ChatPage.js';
import { BotPage } from './pages/BotPage.js';
import { ModeratorPage } from './pages/ModeratorPage.js';
import { DockChatPage } from './pages/DockChatPage.js';
import { WidgetsPage } from './pages/WidgetsPage.js';
import { WidgetRuntimePage } from './pages/WidgetRuntimePage.js';
import { OverlayAlertsPage } from './pages/OverlayAlertsPage.js';
import { ConnectionsPage } from './pages/ConnectionsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SignupPage } from './pages/SignupPage.js';
import { VerifyEmailPage } from './pages/VerifyEmailPage.js';

/**
 * Connaro Console routes (ARCHITECTURE.md §64).
 *
 * Public routes cover the §12 onboarding flow. Everything else sits behind
 * RequireAuth, which is navigation only — the API authorises every request
 * regardless of what the router permits (§4.2).
 */
export function App(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/*
       * OBS dock (§24). Deliberately OUTSIDE the AppShell.
       *
       * Nesting it inside would give the dock the sidebar, the workspace
       * switcher and sign-out, and the "no billing or admin controls in dock
       * mode" requirement would then rest on CSS hiding them — one stylesheet
       * change from exposing account actions in a panel an operator clicks
       * around while running a broadcast. Outside the shell there is nothing
       * to hide.
       *
       * RequireAuth still applies: §24 requires secure authentication, and the
       * MVP dock uses the ordinary session (a scoped device token comes later).
       */}
      <Route
        path="/dock/chat"
        element={
          <RequireAuth>
            <DockChatPage />
          </RequireAuth>
        }
      />

      {/*
       * Deliberately OUTSIDE RequireAuth.
       *
       * An OBS Browser Source has its own cookie jar and runs unattended for
       * hours; requiring an operator session would mean logging in inside OBS
       * and losing every alert the moment that session expired mid-broadcast.
       * Authority comes from the output token instead (Alerts brief §14) —
       * tenant-bound, output-bound, revocable, and carrying nothing but the
       * right to render.
       */}
      <Route path="/overlay/alerts/:outputId" element={<OverlayAlertsPage />} />
      {/*
        * The widget browser source, beside the alert overlay and outside every
        * authenticated layout for the same reason: it renders with no session,
        * no navigation and no Console chrome, and its only authority is the
        * opaque token in `?k=` (§103.6).
        */}
      <Route path="/widget" element={<WidgetRuntimePage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/moderator" element={<ModeratorPage />} />
        <Route path="/bot" element={<BotPage />} />
        <Route path="/alerts" element={<AlertsPage />} />
        <Route path="/widgets" element={<WidgetsPage />} />
        <Route path="/connections" element={<ConnectionsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
